/**
 * Rechtstexte: Impressum, Datenschutzerklärung, Nutzungsbedingungen.
 *
 * Bewusst außerhalb der Partie erreichbar (eigene Routen unterhalb von
 * `/legal`), damit sie auch ohne Anmeldung aufrufbar sind – ein Impressum, das
 * erst nach dem Login sichtbar wird, erfüllt seinen Zweck nicht.
 *
 * HINWEIS: Diese Texte sind Vorlagen, die den konkreten Betrieb dieser Anwendung
 * beschreiben. Sie ersetzen keine rechtliche Prüfung. Vor allem der
 * Datenschutzteil muss zum tatsächlichen Hosting passen – Serverstandort,
 * eingesetzte Dienstleister und Aufbewahrungsfristen sind hier nach dem
 * aktuellen Stand der Anwendung beschrieben und beim ersten Livegang zu prüfen.
 */
import { Link } from 'react-router-dom';

const OPERATOR = {
  name: 'Daniel Stauder',
  street: 'Koblenzer Straße 13',
  city: '66822 Lebach',
  country: 'Deutschland',
  // Bindestriche sind in Objektschlüsseln nicht zulässig – daher `email`.
  email: 'daniel@fam-stauder.de',
} as const;

function LegalLayout({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="mx-auto min-h-screen max-w-3xl px-4 py-10">
      <header className="mb-8">
        <Link to="/" className="text-xs text-panel-500 hover:underline dark:text-panel-400">
          ← Zurück zum Hosting-Simulator
        </Link>
        <h1 className="mt-3 text-2xl font-semibold">{title}</h1>
      </header>
      <div className="space-y-6 text-sm leading-relaxed text-panel-700 dark:text-panel-300">{children}</div>
      <footer className="mt-12 flex flex-wrap gap-4 border-t divider pt-6 text-xs text-panel-500 dark:text-panel-400">
        <Link to="/legal/impressum" className="hover:underline">
          Impressum
        </Link>
        <Link to="/legal/datenschutz" className="hover:underline">
          Datenschutz
        </Link>
        <Link to="/legal/nutzungsbedingungen" className="hover:underline">
          Nutzungsbedingungen
        </Link>
      </footer>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section>
      <h2 className="mb-2 text-base font-semibold text-panel-900 dark:text-panel-100">{title}</h2>
      <div className="space-y-2">{children}</div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Impressum
// ---------------------------------------------------------------------------

export function Impressum() {
  return (
    <LegalLayout title="Impressum">
      <Section title="Angaben gemäß § 5 DDG">
        <address className="not-italic">
          {OPERATOR.name}
          <br />
          {OPERATOR.street}
          <br />
          {OPERATOR.city}
          <br />
          {OPERATOR.country}
        </address>
      </Section>

      <Section title="Kontakt">
        <p>
          E-Mail:{' '}
          <a href={`mailto:${OPERATOR.email}`} className="underline">
            {OPERATOR.email}
          </a>
        </p>
      </Section>

      <Section title="Verantwortlich für den Inhalt nach § 18 Abs. 2 MStV">
        <address className="not-italic">
          {OPERATOR.name}
          <br />
          {OPERATOR.street}
          <br />
          {OPERATOR.city}
        </address>
      </Section>

      <Section title="Streitbeilegung">
        <p>
          Die Europäische Kommission stellt eine Plattform zur Online-Streitbeilegung bereit. Wir sind nicht verpflichtet und
          nicht bereit, an Streitbeilegungsverfahren vor einer Verbraucherschlichtungsstelle teilzunehmen.
        </p>
      </Section>

      <Section title="Haftung für Inhalte">
        <p>
          Als Diensteanbieter sind wir für eigene Inhalte auf diesen Seiten nach den allgemeinen Gesetzen verantwortlich. Wir
          sind jedoch nicht verpflichtet, übermittelte oder gespeicherte fremde Informationen zu überwachen oder nach
          Umständen zu forschen, die auf eine rechtswidrige Tätigkeit hinweisen.
        </p>
        <p>
          Der Hosting-Simulator ist ein Spiel. Sämtliche dargestellten Unternehmen, Netzbetreiber, AS-Nummern, IP-Adressen und
          Internetknoten sind frei erfunden. Ähnlichkeiten mit real existierenden Unternehmen sind nicht beabsichtigt. Die
          verwendeten IP-Bereiche stammen aus den für Dokumentation und Tests reservierten Blöcken.
        </p>
      </Section>

      <Section title="Urheberrecht">
        <p>
          Die durch den Betreiber erstellten Inhalte und Werke unterliegen dem deutschen Urheberrecht. Beiträge Dritter sind
          als solche gekennzeichnet.
        </p>
      </Section>
    </LegalLayout>
  );
}

// ---------------------------------------------------------------------------
// Datenschutz
// ---------------------------------------------------------------------------

export function Datenschutz() {
  return (
    <LegalLayout title="Datenschutzerklärung">
      <Section title="Verantwortlicher">
        <address className="not-italic">
          {OPERATOR.name}
          <br />
          {OPERATOR.street}
          <br />
          {OPERATOR.city}
          <br />
          {OPERATOR.country}
          <br />
          <a href={`mailto:${OPERATOR.email}`} className="underline">
            {OPERATOR.email}
          </a>
        </address>
      </Section>

      <Section title="Grundsatz">
        <p>
          Der Hosting-Simulator ist ein kostenloses Browserspiel. Es werden nur die Daten verarbeitet, die für den Betrieb des
          Spielkontos technisch erforderlich sind. Es findet kein Tracking und keine Weitergabe an Dritte zu Werbezwecken statt.
        </p>
        <p>
          Auf der Startseite befinden sich ein Hinweis auf unseren Partner PawHost sowie eine Einladung zu unserem
          Discord-Server. In beiden Fällen handelt es sich um einfache Verweise ohne Zählpixel, ohne Cookies und ohne
          Einbindung fremder Inhalte – auch das Discord-Zeichen liegt in der Seite selbst und wird nicht von dort geladen.
          Erst wenn Sie einen dieser Verweise anklicken, wird eine Verbindung zum jeweiligen Anbieter aufgebaut; ab diesem
          Zeitpunkt gilt dessen Datenschutzerklärung. Für Discord ist das die Discord Netherlands BV.
        </p>
      </Section>

      <Section title="Welche Daten verarbeitet werden">
        <p>Beim Anlegen und Nutzen eines Spielkontos werden gespeichert:</p>
        <ul className="ml-5 list-disc space-y-1">
          <li>
            <strong>E-Mail-Adresse</strong> – dient als Anmeldename und zur Wiederherstellung des Zugangs.
          </li>
          <li>
            <strong>Anzeigename</strong> – frei wählbar, wird innerhalb des Spiels verwendet.
          </li>
          <li>
            <strong>Passwort</strong> – ausschließlich als kryptografischer Hash. Das Passwort im Klartext ist dem Betreiber
            zu keinem Zeitpunkt bekannt.
          </li>
          <li>
            <strong>Spielstände</strong> – der jeweilige Simulationszustand deiner Partien.
          </li>
          <li>
            <strong>Sitzungsdaten</strong> – kurzlebige Zugangstoken, die die Anmeldung aufrechterhalten.
          </li>
        </ul>
        <p>
          Rechtsgrundlage ist Art. 6 Abs. 1 lit. b DSGVO (Erfüllung des Nutzungsverhältnisses) sowie Art. 6 Abs. 1 lit. f DSGVO
          (berechtigtes Interesse am sicheren Betrieb).
        </p>
      </Section>

      <Section title="Server-Logdateien">
        <p>
          Beim Aufruf werden vom Webserver technische Zugriffsdaten verarbeitet: IP-Adresse, Zeitpunkt, aufgerufene Adresse,
          übertragene Datenmenge, Browsertyp. Sie dienen dem Betrieb und der Abwehr von Angriffen und werden nach spätestens
          sieben Tagen gelöscht. Rechtsgrundlage ist Art. 6 Abs. 1 lit. f DSGVO.
        </p>
      </Section>

      <Section title="Cookies und lokale Speicherung">
        <p>
          Es werden keine Cookies zu Analyse- oder Werbezwecken gesetzt. Für die Anmeldung werden Zugangstoken im lokalen
          Speicher des Browsers abgelegt. Diese sind technisch notwendig; ohne sie wäre keine Anmeldung möglich. Sie lassen
          sich jederzeit durch Abmelden oder Leeren des Browserspeichers entfernen.
        </p>
      </Section>

      <Section title="Hosting">
        <p>
          Die Anwendung wird auf einem angemieteten Server innerhalb der Europäischen Union betrieben. Verbindungen sind per
          TLS verschlüsselt. Mit dem Rechenzentrumsbetreiber besteht ein Auftragsverarbeitungsvertrag nach Art. 28 DSGVO.
        </p>
      </Section>

      <Section title="Speicherdauer">
        <p>
          Kontodaten und Spielstände werden gespeichert, solange das Konto besteht. Nach einer Löschung des Kontos werden sie
          entfernt. Zugriffsprotokolle werden nach sieben Tagen gelöscht.
        </p>
      </Section>

      <Section title="Deine Rechte">
        <p>
          Dir stehen die Rechte auf Auskunft (Art. 15), Berichtigung (Art. 16), Löschung (Art. 17), Einschränkung der
          Verarbeitung (Art. 18), Datenübertragbarkeit (Art. 20) und Widerspruch (Art. 21 DSGVO) zu. Wende dich dafür an{' '}
          <a href={`mailto:${OPERATOR.email}`} className="underline">
            {OPERATOR.email}
          </a>
          .
        </p>
        <p>
          Außerdem steht dir ein Beschwerderecht bei einer Datenschutz-Aufsichtsbehörde zu. Zuständig ist das Unabhängige
          Datenschutzzentrum Saarland.
        </p>
      </Section>

      <Section title="Keine automatisierte Entscheidungsfindung">
        <p>Es findet kein Profiling und keine automatisierte Entscheidungsfindung mit rechtlicher Wirkung statt.</p>
      </Section>
    </LegalLayout>
  );
}

// ---------------------------------------------------------------------------
// Nutzungsbedingungen
// ---------------------------------------------------------------------------

export function Nutzungsbedingungen() {
  return (
    <LegalLayout title="Nutzungsbedingungen">
      <Section title="1. Geltungsbereich">
        <p>
          Diese Bedingungen gelten für die Nutzung des Hosting-Simulators, betrieben von {OPERATOR.name}, {OPERATOR.street},{' '}
          {OPERATOR.city}.
        </p>
      </Section>

      <Section title="2. Kostenlose Nutzung">
        <p>
          <strong>Die Nutzung des Hosting-Simulators ist vollständig kostenlos.</strong> Es gibt keine kostenpflichtigen
          Funktionen, keine Abonnements und keine Käufe innerhalb des Spiels. Sämtliche im Spiel dargestellten Beträge,
          Rechnungen und Zahlungsvorgänge sind Teil der Simulation und haben keinerlei Bezug zu echtem Geld.
        </p>
        <p>
          Das Spiel entsteht in Zusammenarbeit mit dem Hostinganbieter PawHost, auf den die Startseite verweist. Zwischen
          diesem Angebot und den Leistungen von PawHost besteht kein Vertragsverhältnis: Für dort geschlossene Verträge gelten
          ausschließlich deren Bedingungen.
        </p>
        <p>
          Es besteht kein Anspruch auf Verfügbarkeit, Fortbestand oder eine bestimmte Funktion des Angebots. Der Betrieb kann
          jederzeit eingestellt oder verändert werden.
        </p>
      </Section>

      <Section title="3. Spielkonto">
        <p>
          Für die Nutzung ist ein Konto erforderlich. Die Zugangsdaten sind vertraulich zu behandeln. Pro Person ist ein Konto
          vorgesehen. Das Konto ist nicht übertragbar.
        </p>
      </Section>

      <Section title="4. Regeln der Nutzung">
        <p>Untersagt sind insbesondere:</p>
        <ul className="ml-5 list-disc space-y-1">
          <li>der Einsatz von Automatisierung, die den regulären Spielbetrieb umgeht oder den Dienst übermäßig belastet,</li>
          <li>Versuche, die Anwendung, ihre Schnittstellen oder andere Konten zu kompromittieren,</li>
          <li>das Einstellen rechtswidriger, beleidigender oder rechteverletzender Inhalte in frei wählbaren Feldern,</li>
          <li>die Weitergabe von Zugangsdaten an Dritte.</li>
        </ul>
        <p>Bei Verstößen kann der Zugang ohne Vorankündigung gesperrt werden.</p>
      </Section>

      <Section title="5. Spielstände">
        <p>
          Spielstände werden nach bestem Bemühen gespeichert. Ein Anspruch auf Erhalt oder Wiederherstellung besteht nicht.
          Durch Weiterentwicklung der Simulation können bestehende Spielstände angepasst werden; wo möglich geschieht das
          automatisch und verlustfrei.
        </p>
      </Section>

      <Section title="6. Inhalte und Rechte">
        <p>
          Alle im Spiel auftretenden Unternehmen, Netze, AS-Nummern, IP-Bereiche und Internetknoten sind frei erfunden.
          Übereinstimmungen mit realen Unternehmen sind zufällig und nicht beabsichtigt.
        </p>
      </Section>

      <Section title="7. Haftung">
        <p>
          Da das Angebot unentgeltlich bereitgestellt wird, haftet der Betreiber nur für Vorsatz und grobe Fahrlässigkeit sowie
          bei Verletzung von Leben, Körper und Gesundheit. Eine Haftung für Datenverlust ist auf den Aufwand beschränkt, der
          bei ordnungsgemäßer Datensicherung durch die nutzende Person entstanden wäre.
        </p>
      </Section>

      <Section title="8. Beendigung">
        <p>
          Das Nutzungsverhältnis kann jederzeit ohne Angabe von Gründen durch Löschung des Kontos beendet werden. Der Betreiber
          kann das Angebot mit angemessener Ankündigung einstellen.
        </p>
      </Section>

      <Section title="9. Anwendbares Recht">
        <p>
          Es gilt deutsches Recht. Zwingende verbraucherschützende Vorschriften des Staates, in dem die nutzende Person ihren
          gewöhnlichen Aufenthalt hat, bleiben unberührt.
        </p>
      </Section>
    </LegalLayout>
  );
}
